import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Volume2, VolumeX } from "lucide-react";
import fallbackVideo from "@/data/videoDuJour.json";
import ETAPES from "@/data/etapes.json";

// Accueil « La carte du jour » (option H). Mêmes classes (carte.css, préfixe cj-) et même
// structure que scripts/prerendu.js : le passage du HTML pré-rendu à React ne se voit pas.
//
// La « carte » = l'histoire du jour (GET /api/public/carte-du-jour) : on affirme son titre,
// « vrai ou faux ? ». Le site ne publie que des faits vrais et sourcés : la réponse est
// toujours VRAI, et le verso donne l'accroche et les sources. Rien n'est écrit en dur pour
// 1518 : cette histoire n'est que le repli (carte + vidéo) quand l'API ne répond pas.

const MOIS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];

export const dateCourte = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
    return m ? `${Number(m[3])} ${MOIS[Number(m[2]) - 1]} ${m[1]}` : "";
};

export const carteDeRepli = () => ({
    id: fallbackVideo.histoire_id,
    numero: fallbackVideo.numero,
    total: null,
    titre: fallbackVideo.titre,
    domaine: fallbackVideo.domaine,
    accroche: fallbackVideo.accroche,
    slug: fallbackVideo.slug,
    publish_date: fallbackVideo.publish_date,
    sources: fallbackVideo.sources,
    video: {
        mp4: fallbackVideo.mp4,
        poster: fallbackVideo.poster,
        largeur: fallbackVideo.largeur,
        hauteur: fallbackVideo.hauteur,
        duree_s: fallbackVideo.duree_s,
    },
    precedentes: [],
});

// Données posées par le pré-rendu dans la page (pas de carte blanche avant la réponse de l'API).
export const carteInitiale = () => {
    try {
        const el = document.getElementById("carte-data");
        if (el) return JSON.parse(el.textContent);
    } catch {
        /* JSON illisible : repli */
    }
    return carteDeRepli();
};

const nomSource = (s) => String(s || "").replace(/\s+/g, " ").trim();

// ---------------------------------------------------------------------------
// La preuve : la vidéo (muette, se lance quand elle est visible, poster tardif)
// ---------------------------------------------------------------------------
export const VideoPreuve = ({ carte }) => {
    const propre = !!carte.video;
    const v = carte.video || {
        mp4: fallbackVideo.mp4,
        poster: fallbackVideo.poster,
        largeur: fallbackVideo.largeur,
        hauteur: fallbackVideo.hauteur,
        duree_s: fallbackVideo.duree_s,
    };
    const titre = propre ? carte.titre : fallbackVideo.titre;
    const slug = propre ? carte.slug : fallbackVideo.slug;
    const ref = useRef(null);
    const [muet, setMuet] = useState(true);
    const [charge, setCharge] = useState(false);
    const [proche, setProche] = useState(false);
    const [joue, setJoue] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") return undefined;
        const calme =
            typeof window.matchMedia === "function" &&
            window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const obs = new IntersectionObserver(
            ([entree]) => {
                if (entree.isIntersecting) {
                    setCharge(true);
                    if (!calme && entree.intersectionRatio >= 0.5) {
                        const p = el.play();
                        if (p && p.catch) p.catch(() => {});
                    }
                } else {
                    el.pause();
                }
            },
            { threshold: [0, 0.5] },
        );
        // La vidéo (≈ 10 Mo) ne se charge qu'une seconde après la fin du chargement de la page :
        // elle ne ralentit ni les scripts, ni les polices, ni l'affichage de la carte.
        let minuteur = null;
        const demarrer = () => {
            minuteur = setTimeout(() => obs.observe(el), 1000);
        };
        if (document.readyState === "complete") demarrer();
        else window.addEventListener("load", demarrer, { once: true });
        // Le poster ne part qu'à ~200 px de l'écran : pas de concurrence avec le texte du haut.
        const proximite = new IntersectionObserver(
            ([e]) => {
                if (e.isIntersecting) {
                    setProche(true);
                    proximite.disconnect();
                }
            },
            { rootMargin: "200px 0px" },
        );
        proximite.observe(el);
        return () => {
            clearTimeout(minuteur);
            window.removeEventListener("load", demarrer);
            obs.disconnect();
            proximite.disconnect();
        };
    }, []);

    const basculerSon = () => {
        const el = ref.current;
        if (!el) return;
        el.muted = !el.muted;
        setMuet(el.muted);
        if (!el.muted && el.paused) {
            const p = el.play();
            if (p && p.catch) p.catch(() => {});
        }
    };

    return (
        <aside className="cj-preuve" data-testid="video-du-jour" aria-label={`La preuve en vidéo : ${titre}`}>
            <span className="cj-pince" aria-hidden="true" />
            <div className="cj-carte">
                <div className={`cj-cadre${joue ? " joue" : ""}`}>
                    <video
                        ref={ref}
                        data-testid="video-du-jour-lecteur"
                        poster={proche ? v.poster : undefined}
                        width={v.largeur}
                        height={v.hauteur}
                        muted
                        loop
                        playsInline
                        controls
                        controlsList="nodownload"
                        preload="none"
                        aria-label={`Vidéo : ${titre}`}
                        onPlaying={() => setJoue(true)}
                        onVolumeChange={(e) => setMuet(e.currentTarget.muted)}
                    >
                        {charge ? <source src={v.mp4} type="video/mp4" /> : null}
                        Ton navigateur ne lit pas la vidéo. <a href={v.mp4}>Ouvrir la vidéo</a>
                    </video>
                    {v.duree_s ? <span className="cj-play cj-lab">{v.duree_s} s</span> : null}
                    <button
                        type="button"
                        onClick={basculerSon}
                        data-testid="video-son"
                        aria-label={muet ? "Activer le son" : "Couper le son"}
                        className="cj-son"
                    >
                        {muet ? <VolumeX className="w-3.5 h-3.5" aria-hidden="true" /> : <Volume2 className="w-3.5 h-3.5" aria-hidden="true" />}
                        {muet ? "Son" : "Son activé"}
                    </button>
                </div>
                <div className="cj-leg cj-lab">
                    <span>{propre ? "La preuve" : "À voir aussi"}</span>
                    <span>{muet ? "sans le son" : "avec le son"}</span>
                </div>
            </div>
            {propre ? null : (
                <a href={`/histoires/${slug}`} data-testid="video-lien-histoire" className="cj-lien-preuve">
                    {titre} →
                </a>
            )}
        </aside>
    );
};

// ---------------------------------------------------------------------------
// Le paquet : la carte du jour, puis les histoires publiées juste avant (déjà dans la réponse
// de l'API, aucune requête de plus), puis une dernière carte : l'e-mail de demain.
// Chaque carte se retourne (vrai ou faux ? → verso VRAI + accroche + sources + lien).
// « Carte suivante », flèches ← → et balayage sur téléphone la font glisser.
// ---------------------------------------------------------------------------
const VERDICT = {
    vrai: "Bien vu : c'est vrai.",
    faux: "Eh non… c'est vrai !",
    aucun: "C'est vrai.",
};

// La carte à l'écran : un recto (question) et un verso (réponse), `verso` choisit celui qu'on voit.
const CarteFace = ({ c, num, verso, choix, interactive, onChoisir, onRetour, refs }) => {
    const sources = (c.sources || []).map(nomSource).filter(Boolean).slice(0, 3);
    const date = dateCourte(c.publish_date);
    return (
        <div
            className="cj-haut"
            data-verso={verso ? "true" : "false"}
            data-testid={interactive ? "carte-courante" : undefined}
            aria-hidden={interactive ? undefined : "true"}
            inert={interactive ? undefined : true}
        >
            <div className="cj-inner">
                <div className="cj-face cj-recto" inert={verso}>
                    <div className="cj-tete cj-lab">
                        <b>{num}</b>
                        <span>{[c.domaine, date].filter(Boolean).join(" · ")}</span>
                    </div>
                    <span className="cj-main cj-q">Vrai ou faux ?</span>
                    <h2 className="cj-titre" data-testid="carte-titre">
                        {c.titre}
                    </h2>
                    <div className="cj-choix" role="group" aria-label="Ta réponse">
                        <button ref={refs && refs.premier} type="button" className="cj-btn v" data-testid="carte-vrai" onClick={() => onChoisir && onChoisir("vrai")}>
                            VRAI
                        </button>
                        <button type="button" className="cj-btn" data-testid="carte-faux" onClick={() => onChoisir && onChoisir("faux")}>
                            FAUX
                        </button>
                    </div>
                </div>
                <div className="cj-face cj-verso" inert={!verso} data-testid="carte-verso">
                    <span className="cj-stamp" aria-hidden="true">VRAI</span>
                    <div className="cj-tete v cj-lab">
                        <b>Verso</b>
                        <span>{num}</span>
                    </div>
                    <span ref={refs && refs.verdict} tabIndex={-1} className="cj-main cj-verdict" data-testid="carte-verdict">
                        {VERDICT[choix || "aucun"]}
                    </span>
                    <p className="cj-accroche">{c.accroche}</p>
                    {sources.length ? (
                        <div className="cj-src" data-testid="carte-sources">
                            <b className="cj-lab">Sources</b> · <span>{sources.join(" · ")}</span>
                        </div>
                    ) : null}
                    <div className="cj-verso-actions">
                        <a href={`/histoires/${c.slug || c.id}`} data-testid="carte-lire">Lire l&apos;histoire sourcée →</a>
                        <button type="button" onClick={() => onRetour && onRetour()} data-testid="carte-retour">
                            Revoir la carte
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// Dernière carte du paquet : l'invitation à recevoir celle de demain.
const CarteFin = ({ clone }) => (
    <div className="cj-haut" data-verso="false" data-testid={clone ? undefined : "carte-courante"} aria-hidden={clone ? "true" : undefined} inert={clone ? true : undefined}>
        <div className="cj-inner">
            <div className="cj-face cj-recto cj-fin" data-testid="carte-fin">
                <div className="cj-tete cj-lab">
                    <b>Demain</b>
                    <span>Fin du paquet</span>
                </div>
                <span className="cj-main cj-q">La suite ?</span>
                <h2 className="cj-titre" data-testid="carte-titre">La carte de demain arrive demain matin.</h2>
                <ChampEmail testid="cta-carte" id={clone ? undefined : "email-carte"} />
                <p className="cj-petit">
                    L&apos;histoire du jour par e-mail le matin (7&nbsp;h&nbsp;30). Gratuit. Sans carte bancaire,
                    désinscription en un clic.
                </p>
            </div>
        </div>
    </div>
);

export const Paquet = ({ carte }) => {
    const cartes = [carte, ...(carte.precedentes || [])];
    const n = cartes.length;
    const total = carte.total;
    const [i, setI] = useState(0); // i === n : la carte « e-mail »
    const [verso, setVerso] = useState(false);
    const [choix, setChoix] = useState(null);
    const [partant, setPartant] = useState(null);
    const verdict = useRef(null);
    const premier = useRef(null);
    const monte = useRef(false);
    const toucher = useRef(null);
    const racine = useRef(null);
    const fin = i >= n;
    const courante = cartes[Math.min(i, n - 1)];
    const num = (c) => `Carte ${c.numero}${total && c === carte ? `/${total}` : ""}`;

    useEffect(() => {
        // Le focus suit la carte : sur la réponse quand on la retourne, sur la question au retour.
        if (!monte.current) {
            monte.current = true;
            return;
        }
        const cible = verso ? verdict.current : premier.current;
        if (cible) cible.focus({ preventScroll: true });
    }, [verso]);

    useEffect(() => {
        if (!partant) return undefined;
        const t = setTimeout(() => setPartant(null), 520);
        return () => clearTimeout(t);
    }, [partant]);

    const aller = (cible) => {
        const vers = Math.max(0, Math.min(n, cible));
        if (vers === i) return;
        if (!fin) setPartant({ c: courante, verso, choix, sens: vers > i ? 1 : -1, cle: `${i}-${Date.now()}` });
        else setPartant({ fin: true, sens: vers > i ? 1 : -1, cle: `fin-${Date.now()}` });
        // La carte (et son bouton) va disparaître : le focus clavier reste dans le paquet.
        const a = document.activeElement;
        if (racine.current && a && a.closest && a.closest(".cj-pile")) racine.current.focus({ preventScroll: true });
        monte.current = false; // pas de focus forcé quand la carte change
        setI(vers);
        setVerso(false);
        setChoix(null);
    };

    const touches = {
        onPointerDown: (e) => {
            if (e.pointerType === "touch") toucher.current = e.clientX;
        },
        onPointerUp: (e) => {
            if (toucher.current === null) return;
            const dx = e.clientX - toucher.current;
            toucher.current = null;
            if (dx < -60) aller(i + 1);
            else if (dx > 60) aller(i - 1);
        },
        onPointerCancel: () => {
            toucher.current = null;
        },
    };

    const reste = n - i; // cartes encore sous celle-ci (la carte « e-mail » comprise)
    const sens = partant ? partant.sens : 1;

    return (
        <div
            className="cj-paquet"
            ref={racine}
            tabIndex={-1}
            data-testid="carte-du-jour"
            role="region"
            aria-roledescription="paquet de cartes"
            aria-label="L'histoire du jour et les cartes d'avant"
            onKeyDown={(e) => {
                if (e.target && e.target.tagName === "INPUT") return;
                if (e.key === "Escape" && verso) setVerso(false);
                else if (e.key === "ArrowRight") aller(i + 1);
                else if (e.key === "ArrowLeft") aller(i - 1);
            }}
            {...touches}
        >
            {reste >= 1 ? <div className="cj-dessous a" aria-hidden="true" /> : null}
            {reste >= 2 ? <div className="cj-dessous b" aria-hidden="true" /> : null}
            <div className="cj-pile">
                {partant ? (
                    <div className={`cj-sort ${sens > 0 ? "gauche" : "droite"}`} key={partant.cle}>
                        {partant.fin ? (
                            <CarteFin clone />
                        ) : (
                            <CarteFace c={partant.c} num={num(partant.c)} verso={partant.verso} choix={partant.choix} interactive={false} />
                        )}
                    </div>
                ) : null}
                {fin ? (
                    <CarteFin key="fin" />
                ) : (
                    <CarteFace
                        key={courante.id}
                        c={courante}
                        num={num(courante)}
                        verso={verso}
                        choix={choix}
                        interactive
                        onChoisir={(c) => {
                            setChoix(c);
                            setVerso(true);
                        }}
                        onRetour={() => setVerso(false)}
                        refs={{ premier, verdict }}
                    />
                )}
            </div>
            <div className="cj-controles">
                <span className="cj-main cj-compteur" data-testid="carte-compteur" aria-hidden="true">
                    {fin ? "demain" : n > 1 ? `${i + 1} / ${n}` : ""}
                </span>
                {fin ? (
                    <button type="button" className="cj-suivante" onClick={() => aller(0)} data-testid="carte-debut">
                        ← Revoir le paquet
                    </button>
                ) : (
                    <button type="button" className="cj-suivante" onClick={() => aller(i + 1)} data-testid="carte-suivante">
                        Carte suivante →
                    </button>
                )}
            </div>
            <p className="sr-only" role="status" aria-live="polite">
                {fin ? "Dernière carte : recevoir celle de demain par e-mail." : `Carte ${i + 1} sur ${n} : ${courante.titre}`}
            </p>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Invitation par e-mail (ouvre /register?email=…, déjà prévu pour préremplir)
// ---------------------------------------------------------------------------
export const ChampEmail = ({ id, testid }) => {
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const envoyer = (e) => {
        e.preventDefault();
        const v = email.trim();
        navigate(v ? `/register?email=${encodeURIComponent(v)}` : "/register");
    };
    return (
        <form className="cj-form" action="/register" method="get" onSubmit={envoyer}>
            <input
                id={id}
                name="email"
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                placeholder="ton adresse e-mail"
                aria-label="Ton adresse e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid={`${testid}-email`}
            />
            <button type="submit" data-testid={`${testid}-envoyer`}>RECEVOIR</button>
        </form>
    );
};

export const BandeEmail = ({ id, sansPhrase, testid }) => (
    <div className={`cj-bande${sansPhrase ? " seul" : ""}`} data-testid={testid}>
        {sansPhrase ? null : (
            <p className="cj-phrase">
                La carte de demain, <i>chaque matin</i> par e-mail.
            </p>
        )}
        <ChampEmail id={id} testid={testid} />
        <p className="cj-petit">
            L&apos;histoire du jour par e-mail le matin (7&nbsp;h&nbsp;30). Gratuit. Sans carte bancaire,
            désinscription en un clic.
        </p>
    </div>
);

// ---------------------------------------------------------------------------
// Comment ça marche : les trois étapes d'origine de l'accueil (textes inchangés)
// ---------------------------------------------------------------------------
export const Etapes = () => (
    <section id="comment" className="cj-comment" data-testid="comment-ca-marche" aria-labelledby="cj-comment-titre">
        <h2 id="cj-comment-titre">
            Court, simple, <span>et ça reste.</span>
        </h2>
        <ol className="cj-etapes">
            {ETAPES.map((e, i) => (
                <li key={e.titre}>
                    <span className="cj-lab cj-n">0{i + 1}</span>
                    <h3>{e.titre}</h3>
                    <p>{e.texte}</p>
                </li>
            ))}
        </ol>
    </section>
);
